import { Link } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import { AppShell, PageHeader, Section } from '../../../components/layout';
import { Card, Button } from '../../../components/ui';
import { cn } from '../../../lib/cn';

export default function LessonShow({ student, lesson, course, unit, activities, lesson_summary, book_chapters }) {
  const completedCount = activities.filter((a) => a.completed).length;
  const allCompleted = completedCount === activities.length && activities.length > 0;

  const slotGroups = [
    { key: 'prior_homework', label: 'Prior Homework', items: activities.filter((a) => a.slot === 'prior_homework') },
    { key: 'main', label: 'Activities', items: activities.filter((a) => a.slot === 'main') },
    { key: 'homework', label: 'Homework', items: activities.filter((a) => a.slot === 'homework') },
  ].filter((g) => g.items.length > 0);

  // If no slot grouping matters (all same slot), show flat list
  const showFlat = slotGroups.length <= 1;

  const metaParts = [];
  if (lesson_summary?.estimated_minutes) metaParts.push(`~${lesson_summary.estimated_minutes} min`);
  if (lesson_summary?.total_questions > 0) {
    const counts = [];
    if (lesson_summary.short_answer_count > 0) counts.push(`${lesson_summary.short_answer_count} short answer${lesson_summary.short_answer_count === 1 ? '' : 's'}`);
    if (lesson_summary.multiple_choice_count > 0) counts.push(`${lesson_summary.multiple_choice_count} multiple choice question${lesson_summary.multiple_choice_count === 1 ? '' : 's'}`);
    if (lesson_summary.essay_count > 0) counts.push(`${lesson_summary.essay_count} ${lesson_summary.essay_count === 1 ? 'essay' : 'essays'}`);
    const bucketed = lesson_summary.short_answer_count + lesson_summary.multiple_choice_count + lesson_summary.essay_count;
    const remainder = lesson_summary.total_questions - bucketed;
    if (counts.length > 0) {
      if (remainder > 0) counts.push(`${remainder} other${remainder === 1 ? '' : 's'}`);
      metaParts.push(counts.join(', '));
    } else {
      metaParts.push(`${lesson_summary.total_questions} ${lesson_summary.total_questions === 1 ? 'question' : 'questions'}`);
    }
  }
  if (book_chapters?.length > 0) {
    metaParts.push(book_chapters.length === 1 ? book_chapters[0].title : `${book_chapters.length} chapters`);
  }

  return (
    <AppShell
      student={student}
      scrollTitle={
        <>
          <Link href="/student/dashboard" className="hover:text-blue-600 dark:hover:text-blue-400">Dashboard</Link>
          {'  ›  '}
          <Link href={`/student/courses/${course.id}`} className="hover:text-blue-600 dark:hover:text-blue-400">{course.title}</Link>
          {'  ›  '}
          {lesson.title}
        </>
      }
    >
      <div className="max-w-4xl mx-auto">
        <PageHeader
          backLink={`/student/courses/${course.id}`}
          backLabel={course.title}
          title={lesson.title}
          subtitle={unit?.title}
        />

        {lesson.description && (
          <p className="text-sm text-gray-600 dark:text-gray-300 -mt-4 mb-6 leading-relaxed whitespace-pre-wrap">
            {lesson.description}
          </p>
        )}

        {metaParts.length > 0 && (
          <p className="text-xs text-gray-400 dark:text-gray-500 -mt-4 mb-6 flex flex-wrap gap-x-3 gap-y-1">
            {metaParts.map((part, i) => (
              <span key={i}>{part}</span>
            ))}
          </p>
        )}

        {/* Reading entry point — read the chapter without starting a session */}
        {book_chapters?.length > 0 && (
          <div className="-mt-2 mb-6 flex flex-wrap gap-2">
            {book_chapters.map((chapter) => (
              <Link
                key={chapter.id}
                href={`/student/lessons/${lesson.id}/chapters/${chapter.id}`}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-full px-3 py-1.5 transition-colors"
              >
                <BookOpenIcon className="w-3.5 h-3.5" />
                Read {chapter.title}
              </Link>
            ))}
          </div>
        )}

        {/* Summary */}
        {allCompleted && (
          <Card gradient elevated className="mb-8 text-center py-6">
            <div className="flex items-center justify-center gap-2 text-green-600 dark:text-green-400">
              <CheckCircleIcon className="w-6 h-6" />
              <span className="text-lg font-semibold">Lesson Complete</span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {completedCount} {completedCount === 1 ? 'activity' : 'activities'} completed
            </p>
          </Card>
        )}

        {/* Activities */}
        {showFlat ? (
          <Card padding="none">
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {activities.map((activity) => (
                <ActivityRow key={activity.id} activity={activity} />
              ))}
            </div>
          </Card>
        ) : (
          slotGroups.map((group) => (
            <Section key={group.key} title={group.label}>
              <Card padding="none">
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {group.items.map((activity) => (
                    <ActivityRow key={activity.id} activity={activity} />
                  ))}
                </div>
              </Card>
            </Section>
          ))
        )}

        {activities.length === 0 && (
          <Card className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              No activities in this lesson yet.
            </p>
          </Card>
        )}
      </div>
    </AppShell>
  );
}

function ActivityRow({ activity }) {
  const { t } = useTranslation();
  const { url } = activity;

  const actionLabel = (() => {
    if (activity.completed) return { text: t('common.viewResults'), cls: 'text-green-600 dark:text-green-400' };
    if (activity.in_progress) return { text: `${t('common.continue')} ›`, cls: 'text-blue-600 dark:text-blue-400' };
    return { text: `${t('common.begin')} ›`, cls: 'text-gray-500 dark:text-gray-400' };
  })();

  const content = (
    <div className={cn(
      'px-5 py-4 flex items-center gap-4',
      url && 'hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer'
    )}>
      {/* Status icon */}
      <div className="flex-shrink-0">
        {activity.completed ? (
          <CheckCircleIcon className="w-5 h-5 text-green-500" />
        ) : activity.in_progress ? (
          <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <circle cx="12" cy="12" r="10" />
          </svg>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {activity.title}
        </p>
        <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500 mt-0.5">
          {activity.in_progress && activity.answered_count != null ? (
            <span>{activity.answered_count} of {activity.question_count} answered</span>
          ) : (
            <span>{activity.question_count} {activity.question_count === 1 ? 'question' : 'questions'}</span>
          )}
          {activity.completed && activity.mcq_score && (
            <span className="text-green-600 dark:text-green-400">MCQ: {activity.mcq_score}</span>
          )}
          {activity.needs_review_count > 0 && (
            <span className="text-amber-600 dark:text-amber-400">{activity.needs_review_count} to review</span>
          )}
        </div>
      </div>

      {/* Action */}
      <span className={cn('text-xs font-medium flex-shrink-0', actionLabel.cls)}>
        {actionLabel.text}
      </span>

      <svg className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  );

  if (url) {
    return <Link href={url}>{content}</Link>;
  }
  return content;
}

function CheckCircleIcon({ className }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  );
}

function BookOpenIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  );
}
